import { useMemo } from 'react';

import type { SEOProps } from '@components/SEO';
import { seoConfig } from '@config/seo';
import { getAlternateLocalePaths, getLocaleFromPathname, localizePath } from '@content';
import { buildCanonicalUrl } from '@lib/seo';
import { useLocation } from '@tanstack/react-router';

type PageSeoOptions = Omit<SEOProps, 'canonical'> & {
  canonicalPath?: string;
  canonicalUrl?: string;
};

export default function usePageSeo(options: PageSeoOptions): SEOProps {
  const location = useLocation();
  const locale = getLocaleFromPathname(location.pathname);
  const {
    canonicalPath,
    canonicalUrl,
    title,
    description,
    keywords,
    image,
    type = 'website',
    noIndex = false,
    structuredData,
  } = options;

  const canonical = useMemo(() => {
    if (canonicalUrl) {
      return buildCanonicalUrl(canonicalUrl);
    }

    if (canonicalPath) {
      return buildCanonicalUrl(localizePath(canonicalPath, locale));
    }

    return undefined;
  }, [canonicalPath, canonicalUrl, locale]);

  const alternateLinks = useMemo(() => {
    if (!canonicalPath) {
      return undefined;
    }

    return [
      ...getAlternateLocalePaths(canonicalPath).map((alternate) => ({
        hrefLang: alternate.hrefLang,
        href: buildCanonicalUrl(alternate.path),
      })),
      {
        hrefLang: 'x-default',
        href: buildCanonicalUrl(localizePath(canonicalPath, 'en-IN')),
      },
    ];
  }, [canonicalPath]);

  return useMemo(
    () => ({
      title: title ?? seoConfig.defaultTitle,
      description: description ?? seoConfig.defaultDescription,
      keywords,
      image,
      canonical,
      locale,
      alternateLinks,
      type,
      noIndex,
      structuredData,
    }),
    [alternateLinks, canonical, description, image, keywords, locale, noIndex, structuredData, title, type]
  );
}
